import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type {
  Message,
  MessageWithConversationId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { ConversationTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { and, eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ConversationPresenceRecord } from "../db/collections";
import { PREFETCH_PAGES_AHEAD } from "../db/conversation-list-collection";
import { filterGroupToTanstackExpression } from "../filters/conversation-tanstack-expressions";
import { useQueryOptions } from "../filters/filters-context";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

const CONVERSATIONS_PAGE_QUERY_KEY = "conversations-page";
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Page metadata stored in React Query cache.
 * We store IDs instead of full items so we can use useLiveQuery
 * to get live data from TanStack DB.
 */
interface PageMetadata {
  conversationIds: string[];
  totalCount: number;
  page: number;
}

export function useConversations(page: number): {
  data: ConversationListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const queryClient = useQueryClient();
  const queryOptions = useQueryOptions();
  const filterGroup = queryOptions.filterGroup;
  const encodedFilterGroup = filterGroup
    ? encodeAdvancedFilterGroup(filterGroup)
    : undefined;

  // React Query fetches the page and stores metadata (IDs + totalCount)
  // The actual items are inserted into TanStack DB by fetchPage
  const { data: pageMetadata, isLoading: isFetching } = useQuery({
    queryKey: [CONVERSATIONS_PAGE_QUERY_KEY, page, encodedFilterGroup],
    queryFn: async (): Promise<PageMetadata> => {
      const result = await collections.conversationsList.fetchPage(
        page,
        encodedFilterGroup,
      );
      return {
        conversationIds: result.items.map((item) => item.id),
        totalCount: result.totalCount,
        page: result.page,
      };
    },
    staleTime: STALE_TIME,
  });

  // Stabilize the IDs array to prevent useLiveQuery from re-running on every render
  const conversationIds = useMemo(
    () => pageMetadata?.conversationIds ?? [],
    [pageMetadata?.conversationIds],
  );

  // Create a stable string key for the dependency array
  const idsKey = conversationIds.join(",");

  // Use useLiveQuery to get live data from TanStack DB
  // This makes the table reactive to real-time updates
  // We combine the ID filter (from paginated search results) with the actual filter group
  // so that conversations immediately leave the list when they no longer match filters
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ conversation: collections.conversationsList.collection })
        .where(({ conversation }) => {
          const idFilter = inArray(conversation.id, conversationIds);
          const filterExpr = filterGroup
            ? filterGroupToTanstackExpression(filterGroup, conversation)
            : null;
          return filterExpr ? and(idFilter, filterExpr) : idFilter;
        })
        .orderBy(({ conversation }) => conversation.lastCustomerMessageAt, {
          direction: "desc",
          nulls: "last",
        }),

    [idsKey, encodedFilterGroup, collections.conversationsList.collection],
  );

  // Prefetch next pages using React Query's prefetchQuery
  useMemo(() => {
    for (let i = 1; i <= PREFETCH_PAGES_AHEAD; i++) {
      const pageToLoad = page + i;
      void queryClient.prefetchQuery({
        queryKey: [
          CONVERSATIONS_PAGE_QUERY_KEY,
          pageToLoad,
          encodedFilterGroup,
        ],
        queryFn: async (): Promise<PageMetadata> => {
          const result = await collections.conversationsList.fetchPage(
            pageToLoad,
            encodedFilterGroup,
          );
          return {
            conversationIds: result.items.map((item) => item.id),
            totalCount: result.totalCount,
            page: result.page,
          };
        },
        staleTime: STALE_TIME,
      });
    }
  }, [page, encodedFilterGroup, queryClient, collections.conversationsList]);

  return {
    data: liveResult.data,
    totalCount: pageMetadata?.totalCount ?? 0,
    isLoading: isFetching || liveResult.isLoading,
  };
}

export function useConversation(conversationId: ConversationId): {
  data: ConversationListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  // Use TanStack DB's live query - the data is loaded via loadPage, but we can
  // query the collection directly to find a specific conversation.
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ conversation: collections.conversationsList.collection })
        .where(({ conversation }) => eq(conversation.id, conversationId))
        .findOne(),
    [conversationId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useConversationThreads(conversationId: ConversationId): {
  data: ConversationThread[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  // Use TanStack DB's live query - the collection will fetch on-demand.
  // This avoids duplicating React Query fetches.
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.conversationId, conversationId))
        .orderBy(({ thread }) => thread.createdAt, "asc"),
    [conversationId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useConversationMessages(conversationId: ConversationId): {
  data: MessageWithConversationId[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  // Use TanStack DB's live query - the collection will fetch on-demand.
  // This avoids duplicating React Query fetches.
  // NOTE: The query signature (where + orderBy) must match useThreadMessages
  // so TanStack DB can coalesce them into a single fetch.
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [conversationId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useThreadMessages(
  conversationId: ConversationId,
  threadId: string,
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [conversationId],
  );

  const data = useMemo(
    () => result.data.filter((m) => m.conversationThreadId === threadId),
    [result.data, threadId],
  );

  return { data, isLoading: result.isLoading };
}

export function useConversationTimelineEvents(conversationId: ConversationId): {
  data: ConversationTimelineEventResponse[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ event: collections.timelineEvents })
        .where(({ event }) => eq(event.conversationId, conversationId))
        .orderBy(({ event }) => event.occurredAt, "desc"),
    [conversationId],
  );

  const data = result.data ?? [];

  return { data, isLoading: result.isLoading };
}

export function useConversationPresence(conversationId: ConversationId): {
  data: ConversationPresenceRecord[];
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ presence: collections.presence })
        .where(({ presence }) => eq(presence.conversationId, conversationId)),
    [conversationId],
  );

  return { data: result.data };
}

export function useSingleConversationData(conversationId: ConversationId): {
  conversation: ConversationListItemResponse | undefined;
  threads: ConversationThread[];
  messages: Message[];
  isLoading: boolean;
} {
  const conversationResult = useConversation(conversationId);
  const threadsResult = useConversationThreads(conversationId);
  const messagesResult = useConversationMessages(conversationId);

  return {
    conversation: conversationResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    isLoading:
      threadsResult.isLoading ||
      messagesResult.isLoading ||
      conversationResult.isLoading,
  };
}
